/**
 * API Service for Loud Neighbors
 * Handles all backend API calls to /loud_neighbors endpoints
 */

import { isNative } from '../utils/platform';

export const API_BASE = import.meta.env.VITE_API_BASE_URL
  ? `${import.meta.env.VITE_API_BASE_URL}/loud_neighbors`
  : isNative
    ? 'https://api.loudneighbors.com/loud_neighbors'
    : '/loud_neighbors';

// Native auth token storage — cookies don't work in Capacitor WebView
let _authToken = isNative ? localStorage.getItem('ln_auth_token') : null;

export function setAuthToken(token) {
  _authToken = token;
  if (token) {
    localStorage.setItem('ln_auth_token', token);
  } else {
    localStorage.removeItem('ln_auth_token');
  }
}

export function getAuthToken() {
  return _authToken;
}

async function apiRequest(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;

  const headers = {
    'Content-Type': 'application/json',
    ...(options.headers || {}),
  };

  // On native, use bearer token instead of cookies
  if (_authToken) {
    headers['Authorization'] = `Bearer ${_authToken}`;
  }

  const defaultOptions = {
    credentials: 'include',
    headers,
  };

  const response = await fetch(url, { ...defaultOptions, ...options });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `Request failed with status ${response.status}`);
  }

  return response.json();
}

// ============================================================================
// Auth API
// ============================================================================

export async function checkAuth() {
  return apiRequest('/auth/me');
}

export async function logoutUser() {
  return apiRequest('/auth/logout', { method: 'POST' });
}

export async function deleteAccount() {
  return apiRequest('/auth/account', { method: 'DELETE' });
}

export async function refreshToken() {
  return apiRequest('/auth/refresh', { method: 'POST' });
}

// ============================================================================
// User API
// ============================================================================

export async function completeOnboarding(data) {
  return apiRequest('/user/onboarding', {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function checkUsername(username) {
  return apiRequest(`/user/check-username?username=${encodeURIComponent(username)}`);
}

export async function analyzeUser() {
  return apiRequest('/user/analyze', { method: 'POST' });
}

export async function getGenreProfile() {
  return apiRequest('/user/genre-profile');
}

export async function getProfile() {
  return apiRequest('/user/profile');
}

export async function updateProfile(data) {
  return apiRequest('/user/profile', {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

export async function searchUsers(username) {
  return apiRequest(`/users/search?username=${encodeURIComponent(username)}`);
}

// ============================================================================
// City Search API
// ============================================================================

export async function searchCities(query, limit = 10) {
  return apiRequest(`/cities/search?q=${encodeURIComponent(query)}&limit=${limit}`);
}

// ============================================================================
// Spotify Data API
// ============================================================================

export async function getTopArtists(timeRange = 'medium_term', limit = 50, { fallback = false } = {}) {
  const params = new URLSearchParams({ time_range: timeRange, limit: limit.toString() });
  if (fallback) params.set('fallback', 'true');
  return apiRequest(`/me/top-artists?${params.toString()}`);
}

export async function getTopTracks(timeRange = 'medium_term', limit = 50, { fallback = false } = {}) {
  const params = new URLSearchParams({ time_range: timeRange, limit: limit.toString() });
  if (fallback) params.set('fallback', 'true');
  return apiRequest(`/me/top-tracks?${params.toString()}`);
}

export async function getRecentlyPlayed(limit = 50) {
  return apiRequest(`/me/recently-played?limit=${limit}`);
}

export async function getFollowedArtists(limit = 50, after = null) {
  const params = new URLSearchParams({ limit: limit.toString() });
  if (after) params.append('after', after);
  return apiRequest(`/me/followed-artists?${params.toString()}`);
}

// ============================================================================
// Concerts API
// ============================================================================

export async function getConcerts(city = '', minScore = 0, limit = 50, radiusMiles = 25, daysAhead = 30, lat = null, lng = null) {
  const params = new URLSearchParams({
    city,
    min_score: minScore.toString(),
    limit: limit.toString(),
    radius_miles: radiusMiles.toString(),
    days_ahead: daysAhead.toString(),
  });
  if (lat != null) params.append('lat', lat.toString());
  if (lng != null) params.append('lng', lng.toString());
  return apiRequest(`/concerts?${params.toString()}`);
}

// ============================================================================
// Coachella API
// ============================================================================

export async function getFestivals(year) {
  return apiRequest(`/coachella/${year}/festivals`);
}

export async function getCoachellaActs(year, day = null, festival = 'coachella_weekend_1') {
  const params = new URLSearchParams({ festival });
  if (day) params.append('day', day);
  return apiRequest(`/coachella/${year}/acts?${params.toString()}`);
}

export async function scoreCoachellaLineup(year, festival = 'coachella_weekend_1') {
  return apiRequest(`/coachella/${year}/score?festival=${festival}`, { method: 'POST' });
}

export async function getCoachellaSchedule(year, festival = 'coachella_weekend_1') {
  return apiRequest(`/coachella/${year}/schedule?festival=${festival}`);
}

export async function saveCustomSchedule(year, acts, festival = 'coachella_weekend_1') {
  return apiRequest(`/coachella/${year}/custom-schedule?festival=${festival}`, {
    method: 'POST',
    body: JSON.stringify({ acts }),
  });
}

export async function getCustomSchedule(year, festival = 'coachella_weekend_1') {
  return apiRequest(`/coachella/${year}/custom-schedule?festival=${festival}`);
}

// ============================================================================
// Groups API
// ============================================================================

export async function createGroup(name, festivalName = 'coachella_weekend_1') {
  return apiRequest('/groups', {
    method: 'POST',
    body: JSON.stringify({ name, festival_name: festivalName }),
  });
}

export async function getGroups(festival = null) {
  const params = festival ? `?festival=${festival}` : '';
  return apiRequest(`/groups${params}`);
}

export async function getGroup(groupId) {
  return apiRequest(`/groups/${groupId}`);
}

export async function inviteToGroup(groupId, inviteType, target = {}) {
  return apiRequest(`/groups/${groupId}/invite`, {
    method: 'POST',
    body: JSON.stringify({ invite_type: inviteType, ...target }),
  });
}

export async function joinGroup(inviteCode) {
  return apiRequest(`/groups/join/${inviteCode}`, { method: 'POST' });
}

export async function getGroupCompatibility(groupId) {
  return apiRequest(`/groups/${groupId}/compatibility`);
}

export async function leaveGroup(groupId) {
  return apiRequest(`/groups/${groupId}/leave`, { method: 'POST' });
}

export async function computeGroupSchedule(year, groupId, festival = 'coachella_weekend_1') {
  return apiRequest(`/coachella/${year}/group/${groupId}/schedule?festival=${festival}`, { method: 'POST' });
}

export async function getGroupSchedule(year, groupId, festival = 'coachella_weekend_1') {
  return apiRequest(`/coachella/${year}/group/${groupId}/schedule?festival=${festival}`);
}

export async function saveGroupCustomSchedule(year, groupId, acts, festival = 'coachella_weekend_1') {
  return apiRequest(`/coachella/${year}/group/${groupId}/custom-schedule?festival=${festival}`, {
    method: 'POST',
    body: JSON.stringify({ acts }),
  });
}

// ============================================================================
// Leaderboards API
// ============================================================================

export async function getArtistLeaderboard(artistSpotifyId, week = 'current') {
  return apiRequest(`/leaderboards/artist/${artistSpotifyId}?week=${week}`);
}

export async function getMyLeaderboards() {
  return apiRequest('/leaderboards/me');
}

// Device / Push Notifications

export async function registerDeviceToken(token, platform) {
  return apiRequest('/device/register', {
    method: 'POST',
    body: JSON.stringify({ token, platform }),
  });
}

export async function unregisterDeviceToken(token) {
  return apiRequest('/device/unregister', {
    method: 'DELETE',
    body: JSON.stringify({ token }),
  });
}
